import React, { useState, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Eye, EyeOff, Zap, AlertTriangle } from "lucide-react";

const TEST_USERS = [
  { label: "Ultra Admin",  email: "admin@expostand.com",          password: "Admin123!", color: "bg-orange-500 hover:bg-orange-600 text-white" },
  { label: "Exhibitor",    email: "demo_exhibitor@expostand.com", password: "expo1234",  color: "bg-blue-500 hover:bg-blue-600 text-white" },
  { label: "Contractor",   email: "demo_contractor@expostand.com",password: "expo1234",  color: "bg-emerald-600 hover:bg-emerald-700 text-white" },
  { label: "Supplier",     email: "demo_supplier@expostand.com",  password: "expo1234",  color: "bg-purple-600 hover:bg-purple-700 text-white" },
];

const AdminLogin = () => {
  const [formData, setFormData] = useState({ email: "", password: "", rememberMe: false });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [attemptCount, setAttemptCount] = useState(0);

  const { login, loading, error: authError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const MAX_ATTEMPTS = 5;
  const isBlocked = attemptCount >= MAX_ATTEMPTS;

  const validateEmail = (email) => {
    if (!email.trim()) return "Email is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.toLowerCase().trim())) return "Please enter a valid email address";
    return "";
  };

  const validatePassword = (password) => {
    if (!password) return "Password is required";
    if (password.length < 6) return "Password must be at least 6 characters";
    return "";
  };

  const handleBlur = (field, value) => {
    const err = field === 'email' ? validateEmail(value) : validatePassword(value);
    setErrors(prev => ({ ...prev, [field]: err }));
  };

  const handleInputChange = (field, value) => {
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: "" }));
    setFormData(prev => ({ ...prev, [field]: field === 'email' ? value.toLowerCase().trim() : value }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (isBlocked) return;
    const emailErr = validateEmail(formData.email);
    const pwErr = validatePassword(formData.password);
    setErrors({ email: emailErr, password: pwErr });
    if (emailErr || pwErr) return;
    try {
      const result = await login(formData.email, formData.password, formData.rememberMe);
      if (result.success) {
        setAttemptCount(0);
        navigate(location.state?.from?.pathname || '/', { replace: true });
      } else {
        setAttemptCount(prev => prev + 1);
      }
    } catch { setAttemptCount(prev => prev + 1); }
  };

  const quickLogin = async (email, password) => {
    try {
      const result = await login(email, password, false);
      if (result.success) navigate(location.state?.from?.pathname || '/', { replace: true });
    } catch { /* ignore */ }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* ── Left brand panel ── */}
      <div className="hidden lg:flex lg:w-[58%] relative flex-col justify-between p-12 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #1C1917 50%, #292524 100%)' }}>
        {/* Background blobs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-10" style={{ background: '#F97316', filter: 'blur(100px)', transform: 'translate(30%, -30%)' }} />
          <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full opacity-10" style={{ background: '#EA580C', filter: 'blur(80px)', transform: 'translate(-30%, 30%)' }} />
          <svg className="absolute inset-0 w-full h-full opacity-5" xmlns="http://www.w3.org/2000/svg">
            <defs><pattern id="grid-a" width="40" height="40" patternUnits="userSpaceOnUse"><path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="1"/></pattern></defs>
            <rect width="100%" height="100%" fill="url(#grid-a)" />
          </svg>
        </div>

        {/* Two-column content */}
        <div className="relative z-10 flex gap-12 flex-1">
          {/* Left: brand + copy */}
          <div className="flex-1 flex flex-col justify-center">
            <a href="/" className="flex items-center gap-3 mb-12">
              <img src="https://boothshop.my.canva.site/master-file/images/f240dd7de334359f1526183865a27145.svg" alt="EXPO STAND" className="w-10 h-9 opacity-90" />
              <div>
                <div className="text-white font-bold text-lg leading-tight">EXPO STAND</div>
                <div className="text-orange-400 text-xs">Global Exhibition Network</div>
              </div>
            </a>

            <h1 className="text-5xl font-bold text-white leading-tight mb-4">
              Ultra<br /><span className="text-orange-400">Admin</span>
            </h1>
            <p className="text-stone-300 text-base mb-10 max-w-xs">
              Full platform control. Manage organizations, users, plans, and subscriptions from one place.
            </p>

            <div className="space-y-4">
              {[
                { icon: '◈', text: 'Complete dashboard with revenue analytics' },
                { icon: '◎', text: 'Organization & user management' },
                { icon: '◉', text: 'Subscription plans & billing control' },
                { icon: '◐', text: 'Audit logs, webhooks & system settings' },
              ].map(item => (
                <div key={item.text} className="flex items-center gap-3">
                  <span className="text-orange-400 text-lg w-6 shrink-0">{item.icon}</span>
                  <span className="text-stone-300 text-sm">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: floating stat cards */}
          <div className="w-56 flex flex-col justify-center gap-4 shrink-0">
            {[
              { val: '3,600+', lbl: 'Total Users', icon: '◈', color: 'from-orange-500/20 to-orange-600/10' },
              { val: '142', lbl: 'Organizations', icon: '◎', color: 'from-amber-500/20 to-amber-600/10' },
              { val: '€1.2M', lbl: 'Monthly Revenue', icon: '◉', color: 'from-orange-400/20 to-orange-500/10' },
              { val: '99.9%', lbl: 'Uptime', icon: '◐', color: 'from-stone-500/20 to-stone-600/10' },
            ].map(card => (
              <div key={card.lbl}
                className={`rounded-2xl border border-white/10 bg-gradient-to-br ${card.color} backdrop-blur-sm p-4`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-orange-400 text-base">{card.icon}</span>
                  <span className="text-white text-xl font-bold">{card.val}</span>
                </div>
                <div className="text-orange-300 text-xs pl-6">{card.lbl}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom tagline */}
        <div className="relative z-10 pt-8 border-t border-stone-700/50">
          <p className="text-stone-500 text-xs">"Built for operators who demand full visibility and control"</p>
        </div>
      </div>

      {/* ── Right form panel ── */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-10 bg-white overflow-y-auto">
        {/* Mobile logo */}
        <a href="/" className="flex items-center gap-2 mb-6 lg:hidden">
          <img src="https://boothshop.my.canva.site/master-file/images/f240dd7de334359f1526183865a27145.svg" alt="EXPO STAND" className="w-8 h-7" />
          <span className="font-bold text-gray-800">EXPO STAND</span>
        </a>

        <div className="w-full max-w-sm">
          <div className="mb-7">
            <h2 className="text-2xl font-bold text-gray-900">Admin Sign In</h2>
            <p className="text-sm text-gray-500 mt-1">Sign in to your admin account</p>
          </div>

          {isBlocked && (
            <div className="mb-4 flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              Too many failed attempts. Please wait before trying again.
            </div>
          )}

          {authError && !isBlocked && (
            <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl">
              Invalid email or password.
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Email</label>
              <input
                type="email" value={formData.email}
                onChange={e => handleInputChange('email', e.target.value)}
                onBlur={e => handleBlur('email', e.target.value)}
                required disabled={isBlocked}
                placeholder="admin@expostand.com"
                data-testid="login-email"
                className={`w-full px-4 py-3 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition ${errors.email ? 'border-red-300' : 'border-gray-200'}`}
              />
              {errors.email && <p className="text-red-600 text-xs mt-1">{errors.email}</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={e => handleInputChange('password', e.target.value)}
                  onBlur={e => handleBlur('password', e.target.value)}
                  required disabled={isBlocked}
                  placeholder="••••••••"
                  data-testid="login-password"
                  className={`w-full px-4 py-3 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition pr-11 ${errors.password ? 'border-red-300' : 'border-gray-200'}`}
                />
                <button type="button" onClick={() => setShowPassword(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.password && <p className="text-red-600 text-xs mt-1">{errors.password}</p>}
            </div>
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer">
                <input type="checkbox" checked={formData.rememberMe}
                  onChange={e => setFormData(prev => ({ ...prev, rememberMe: e.target.checked }))}
                  className="rounded border-gray-300" data-testid="login-remember" />
                Remember me
              </label>
              <button type="button" onClick={() => navigate('/auth/forgot-password')}
                className="text-xs text-orange-600 hover:underline" data-testid="login-forgot">
                Forgot password?
              </button>
            </div>
            <button type="submit" disabled={loading || isBlocked}
              data-testid="login-submit"
              className="w-full py-3 rounded-xl font-semibold text-white text-sm transition-all disabled:opacity-60"
              style={{ background: loading || isBlocked ? '#FDB98A' : 'linear-gradient(135deg, #EA580C, #F97316)' }}>
              {loading ? 'Signing in...' : 'Sign In →'}
            </button>
          </form>

          {attemptCount > 0 && attemptCount < MAX_ATTEMPTS && (
            <p className="mt-2 text-center text-xs text-amber-600">
              {MAX_ATTEMPTS - attemptCount} attempts remaining
            </p>
          )}

          {/* Quick Login */}
          <div className="mt-6 pt-5 border-t border-gray-100">
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-4 h-4 text-amber-500" />
              <span className="text-xs font-semibold text-gray-600">Quick Access — One-Click Login</span>
            </div>
            <div className="space-y-2">
              {TEST_USERS.map(u => (
                <button key={u.email} type="button"
                  onClick={() => quickLogin(u.email, u.password)}
                  disabled={loading}
                  data-testid={`quick-login-${u.label.toLowerCase().replace(' ', '-')}`}
                  className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${u.color} disabled:opacity-60`}>
                  <span className="font-semibold">{u.label}</span>
                  <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">{u.email}</span>
                </button>
              ))}
            </div>
          </div>

          <p className="mt-6 text-center">
            <a href="/" className="text-xs text-gray-400 hover:text-gray-600">← Back to home</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
